// frontend/src/components/PrintPackingSlip.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Select,
  MenuItem,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
  LinearProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';

const clients = [
  { company_ref_id: 24388, name: 'Funky Pigeon' },
  { company_ref_id: 850108, name: 'Jura' },
  { company_ref_id: 702781, name: 'Echo Three Retailer Dropship' },
  { company_ref_id: 690448, name: 'Echo Three Etsy Dropship' },
  { company_ref_id: 349555, name: 'Danilo (Amazon) - IV Creative Limited B2C Supplier' },
].sort((a, b) => a.name.localeCompare(b.name));

const EXCLUDE_OPTIONS = ['GIANTCARD', 'NUTELLA350GJAR', 'LINDT143GBOX'];
const ITEMS_PER_BATCH = 35;

function OrderRow({ order, isSelected, toggle }) {
  const [open, setOpen] = useState(false);
  const totalQty = order.items.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => toggle(order.id)}
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{order.id}</TableCell>
        <TableCell>{order.external_ref}</TableCell>
        <TableCell>{order.customer_name}</TableCell>
        <TableCell>{new Date(order.creation_datetime).toLocaleString()}</TableCell>
        <TableCell>{totalQty}</TableCell>
        <TableCell>{order.shipping_carrier}</TableCell>
        <TableCell>{order.shipping_method}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 0.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Items
              </Typography>
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& th, & td': { py: 0.5, px: 1 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Thumbnail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.items.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.mapped_sku}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {item.thumbnail?.url && (
                          <img
                            src={item.thumbnail.url}
                            alt={item.mapped_sku}
                            style={{ width: 40, height: 40, objectFit: 'cover' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

OrderRow.propTypes = {
  order: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

export default function PrintPackingSlip() {
  const [companyRefId, setCompanyRefId] = useState(clients[0].company_ref_id);
  const [filterType, setFilterType] = useState('unprinted');
  const [orders, setOrders] = useState([]);
  const [excludeSkus, setExcludeSkus] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [totalItems, setTotalItems] = useState(0);

  const selectedClient = clients.find(c => c.company_ref_id === companyRefId)?.name;

  // derive filtered & displayed
  const filtered = useMemo(
    () => orders.filter(o =>
      !o.items.items.some(i => excludeSkus.includes(i.sku))
    ),
    [orders, excludeSkus]
  );
  useEffect(() => {
    setTotalItems(filtered.length);
  }, [filtered]);
  const displayed = filtered.slice(0, ITEMS_PER_BATCH);

  // whenever a new batch of orders is loaded, reset selection to all of them
  useEffect(() => {
    setSelected(orders.map(o => o.id));
  }, [orders]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/linx-dispatch/kornitx-production-orders', {
        params: { companyRefId, filterType }
      });
      if (data.success) {
        setOrders(data.orders);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = id => {
    setSelected(sel =>
      sel.includes(id)
        ? sel.filter(x => x !== id)
        : [...sel, id]
    );
  };

  const displayedIds = displayed.map(o => o.id);
  const allDisplayedSelected = displayedIds.every(id => selected.includes(id));
  const toggleAll = () => {
    setSelected(allDisplayedSelected ? [] : displayedIds);
  };

  const printPackingSlip = async () => {
    const toPrint = orders
      .filter(o => selected.includes(o.id))
      .slice(0, ITEMS_PER_BATCH);
    if (!toPrint.length) return;

    setOverlay(true);
    setProgress({ loaded: 0, total: toPrint.length });
    const timer = setInterval(() => {
      setProgress(p => ({
        loaded: Math.min(p.loaded + 1, p.total),
        total: p.total
      }));
    }, 750);

    try {
      const res = await axios.post(
        '/api/linx-dispatch/print-packing-slip',
        { ordersData: toPrint, clientName: selectedClient },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      clearInterval(timer);
      setOverlay(false);
    }
  };

  return (
    <Paper sx={{ p: 3, m: 2, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>
        📃 Print KornitX Packing Slips
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        *Only “In Production” status orders will be loaded.*
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Client</InputLabel>
          <Select
            value={companyRefId}
            onChange={e => setCompanyRefId(e.target.value)}
            input={<OutlinedInput label="Client" />}
          >
            {clients.map(c => (
              <MenuItem key={c.company_ref_id} value={c.company_ref_id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Exclude SKUs</InputLabel>
          <Select
            multiple
            value={excludeSkus}
            onChange={e => setExcludeSkus(e.target.value)}
            input={<OutlinedInput label="Exclude SKUs" />}
            renderValue={vals => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {vals.map(v => <Chip key={v} label={v} size="small" />)}
              </Box>
            )}
          >
            {EXCLUDE_OPTIONS.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Show</InputLabel>
          <Select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            input={<OutlinedInput label="Show" />}
          >
            <MenuItem value="unprinted">Unprinted</MenuItem>
            <MenuItem value="printed">Printed</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={fetchOrders}
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          Load Orders
        </Button>
        <Button
          variant="outlined"
          onClick={printPackingSlip}
          disabled={!selected.length}
        >
          Print Packing Slips
        </Button>
      </Box>

      <Box sx={{
        mb: 2, p: 2, bgcolor: 'background.default',
        borderRadius: 2, boxShadow: theme => `0 0 3px ${theme.palette.divider}`
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Orders To Be Processed: {' '}
          <Typography variant="inherit" component="span" color="primary">
            {totalItems}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only the first {ITEMS_PER_BATCH} orders will be included in this batch.
        </Typography>
      </Box>

      <TableContainer sx={{ maxHeight: 600, '& th, & td': { py: 0.5, px: 1 } }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selected.length > 0 && !allDisplayedSelected}
                  checked={allDisplayedSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell />
              <TableCell>ID</TableCell>
              <TableCell>External Ref</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total Qty</TableCell>
              <TableCell>Courier</TableCell>
              <TableCell>Method</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.map(order => (
              <OrderRow
                key={order.id}
                order={order}
                isSelected={selected.includes(order.id)}
                toggle={toggleOne}
              />
            ))}
            {!displayed.length && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No orders to display.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Backdrop open={overlay} sx={{ color: '#fff', zIndex: 1000 }}>
        <Box textAlign="center">
          <Typography gutterBottom>
            Generating {progress.loaded} / {progress.total}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(progress.loaded / progress.total) * 100}
            sx={{ width: 300 }}
          />
        </Box>
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
}
